import React, { useEffect, useRef, useState } from "react";
import CommonTable from "../../../components/Table/Table";
import "./buyerRequireList.css";
import { useNavigate } from "react-router-dom";
import {
  getAllBuyerRequirementApi,
  getAllBuyerEnquiriesApi,
  getExactMatchSellersApi,
  shareRequirementWithSellers,
  getRequirementResponseSummaryApi,
  respondRequirementApi,
  adminRespondRequirementApi,
  deleteBuyerRequirementApi,
} from "../../../services/buyerRequirement/buyerRequirement.service";
import { getAllDesired } from "../../../services/DesiredType/DesiredType.service";
import { Eye, Send, CheckCircle, XCircle, Clock, Calculator, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import DealCalculatorModal from "../../../components/DealCalculatorModal";
import { environment } from "../../../environments/environment";

const BuyerRequirement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"requirements" | "enquiries">("requirements");
  const [search, setSearch] = useState("");
  const [requirements, setRequirements] = useState<any[]>([]);
  const [enquiries, setEnquiries] = useState<any[]>([]);
  const [selectedReq, setSelectedReq] = useState<any | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedRequirementForShare, setSelectedRequirementForShare] = useState<any | null>(null);
  const [selectedSellers, setSelectedSellers] = useState<string[]>([]);
  const [metalList, setMetalList] = useState<any[]>([]);
  const [matchedSellers, setMatchedSellers] = useState<any[]>([]);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const calledOnce = useRef(false);
  const navigate = useNavigate();

  const [showDealCalculatorModal, setShowDealCalculatorModal] = useState(false);
  const [selectedRowForDealCalculator, setSelectedRowForDealCalculator] = useState<any | null>(null);
  const [acceptedSellersForDeal, setAcceptedSellersForDeal] = useState<any[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteRequirementId, setDeleteRequirementId] = useState<number | null>(null);

  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseSummary, setResponseSummary] = useState<any>(null);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [selectedRequirementForResponse, setSelectedRequirementForResponse] = useState<any | null>(null);

  const [sharedRequirements, setSharedRequirements] = useState<Set<number>>(new Set());

  // ---------- Helper to get article name (with fallback to matchedItems) ----------
  const getArticleName = (row: any): string => {
    if (row.article_name) return row.article_name;
    // fallback: try to get from first matched item (if any)
    if (row.matchedItems && row.matchedItems.length > 0 && row.matchedItems[0].articleName) {
      return row.matchedItems[0].articleName;
    }
    return "-";
  };

  // ---------- API calls ----------
  const loadRequirements = async () => {
    try {
      const res = await getAllBuyerRequirementApi();
      if (res?.data) {
        setRequirements(res.data);
        loadSharedRequirements();
      }
    } catch (error) {
      console.error("Error fetching buyer requirements:", error);
      setRequirements([]);
    }
  };

  const loadEnquiries = async () => {
    try {
      const res = await getAllBuyerEnquiriesApi();
      if (res?.data) {
        setEnquiries(res.data);
      }
    } catch (error) {
      console.error("Error fetching buyer enquiries:", error);
      setEnquiries([]);
    }
  };

  const loadSharedRequirements = () => {
    try {
      const saved = localStorage.getItem("sharedRequirements");
      if (saved) {
        setSharedRequirements(new Set(JSON.parse(saved)));
      }
    } catch (error) {
      console.error("Error loading shared requirements:", error);
    }
  };

  const getRequirementPurity = (row: any): string => {
    const candidates = [
      row?.Purity,
      row?.purity,
      row?.requirement?.Purity,
      row?.requirement?.purity,
      row?.matchedItem?.Purity,
      row?.matchedItem?.purity,
    ];

    for (const candidate of candidates) {
      if (candidate === null || candidate === undefined) continue;
      const value = String(candidate).trim();
      if (value) return value;
    }

    return "-";
  };

  const getRequirementUom = (row: any): string => {
    return row?.uomDetails?.name || row?.UomName || row?.requirement?.UomName || row?.product?.Uom?.name || "-";
  };

  const syncRequirementFromResponse = (sellers: any[]) => {
    const responseRequirement = sellers.find((seller: any) => seller.requirement)?.requirement;
    if (!responseRequirement?.id) return;

    setRequirements((prev) =>
      prev.map((item) =>
        item.id === responseRequirement.id
          ? { ...item, ...responseRequirement }
          : item
      )
    );

    setSelectedRequirementForResponse((prev: any) =>
      prev?.id === responseRequirement.id ? { ...prev, ...responseRequirement } : prev
    );
    setSelectedReq((prev: any) =>
      prev?.id === responseRequirement.id ? { ...prev, ...responseRequirement } : prev
    );
    setSelectedRequirementForShare((prev: any) =>
      prev?.id === responseRequirement.id ? { ...prev, ...responseRequirement } : prev
    );
  };

  const saveSharedRequirement = (requirementId: number) => {
    try {
      const newSet = new Set(sharedRequirements);
      newSet.add(requirementId);
      setSharedRequirements(newSet);
      localStorage.setItem("sharedRequirements", JSON.stringify(Array.from(newSet)));
    } catch (error) {
      console.error("Error saving shared requirement:", error);
    }
  };

  const loadMetals = async () => {
    try {
      const res = await getAllDesired();
      if (Array.isArray(res.data)) {
        setMetalList(res.data);
      }
    } catch (error) {
      console.error("Error fetching metals:", error);
    }
  };

  const loadResponseSummary = async (requirementId: number) => {
    try {
      setLoadingResponses(true);
      const res = await getRequirementResponseSummaryApi(requirementId);
      if (res?.success && res.summary) {
        const allSellers = [
          ...(res.sellers?.accepted || []),
          ...(res.sellers?.rejected || []),
          ...(res.sellers?.pending || []),
        ];
        const formattedResponse = {
          total: res.summary.total || 0,
          accepted: res.summary.accepted || 0,
          rejected: res.summary.rejected || 0,
          pending: res.summary.pending || 0,
          sellers: allSellers,
        };
        setResponseSummary(formattedResponse);
        syncRequirementFromResponse(allSellers);
      } else if (res?.data?.success && res.data.summary) {
        const apiData = res.data;
        const allSellers = [
          ...(apiData.sellers?.accepted || []),
          ...(apiData.sellers?.rejected || []),
          ...(apiData.sellers?.pending || []),
        ];
        const formattedResponse = {
          total: apiData.summary.total || 0,
          accepted: apiData.summary.accepted || 0,
          rejected: apiData.summary.rejected || 0,
          pending: apiData.summary.pending || 0,
          sellers: allSellers,
        };
        setResponseSummary(formattedResponse);
        syncRequirementFromResponse(allSellers);
      } else {
        setResponseSummary(null);
        toast.error("Invalid response format from server");
      }
    } catch (error) {
      console.error("Error loading response summary:", error);
      toast.error("Failed to load seller responses");
      setResponseSummary(null);
    } finally {
      setLoadingResponses(false);
    }
  };

  useEffect(() => {
    if (calledOnce.current) return;
    calledOnce.current = true;
    loadRequirements();
    loadEnquiries();
    loadMetals();
  }, []);

  const getMetalName = (id?: number) => {
    const metal = metalList.find((m) => m.id === id);
    return metal ? metal.name : "-";
  };

  const getPurityLabel = (metalTypeId: number, categoryName: string) => {
    if (metalTypeId === 1) return "22K";
    if (metalTypeId === 2 || metalTypeId === 3) return "999";
    return "-";
  };

  const getBuyerInfo = (row: any) => {
    if (row?.buyerDetails) {
      return {
        name: row.buyerDetails.firstName || "Unknown Buyer",
        phone: row.buyerDetails.mobileNumber || "-",
        businessName: row.buyerDetails.businessName,
      };
    }
    if (row?.user) {
      return {
        name: row.user.businessName || `${row.user.firstName || ""} ${row.user.lastName || ""}`.trim() || "Unknown Buyer",
        phone: row.user.mobile_number || "-",
      };
    }
    return { name: "Unknown Buyer", phone: "-" };
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { class: string; icon: any; label: string }> = {
      Active: { class: "status-inprocess", icon: Clock, label: "In Process" },
      InProcess: { class: "status-inprocess", icon: Clock, label: "In Process" },
      Matched: { class: "status-matched", icon: CheckCircle, label: "Matched" },
      Delivered: { class: "status-delivered", icon: CheckCircle, label: "Delivered" },
      Cancelled: { class: "status-cancelled", icon: XCircle, label: "Cancelled" },
    };
    const config = statusConfig[status] || statusConfig["Active"];
    const Icon = config.icon;
    return (
      <span className={`status-badge ${config.class}`}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  const loadMatchedSellers = async (requirementId: number) => {
    try {
      setLoadingSellers(true);
      const res = await getExactMatchSellersApi(requirementId);
      if (res?.data && Array.isArray(res.data)) {
        const sellers = res.data.map((item: any) => item.buyers_sellers_detail).filter(Boolean);
        setMatchedSellers(sellers);
      } else {
        setMatchedSellers([]);
      }
    } catch (error) {
      console.error("Error fetching matched sellers:", error);
      setMatchedSellers([]);
    } finally {
      setLoadingSellers(false);
    }
  };

  const handleShareClick = async (row: any) => {
    setSelectedRequirementForShare(row);
    setShowShareModal(true);
    setSelectedSellers([]);
    setMatchedSellers([]);
    await loadMatchedSellers(row.id);
  };
const handleEnquirySendClick = (row: any) => {
  // Extract data from enquiry row
  const enquiry = row;
  const product = enquiry.product;
  if (!product) {
    toast.error("Product details missing");
    return;
  }
  const dealData = {
    buyerId: enquiry.userID,
    buyerName: enquiry.user?.businessName || `${enquiry.user?.firstName || ''} ${enquiry.user?.lastName || ''}`.trim(),
    sellerId: product.userid,
    sellerName: product.user?.businessName || `${product.user?.firstName || ''} ${product.user?.lastName || ''}`.trim(),
    metalType: product.Metal_type?.name,
    category: product.category?.name,
    articleName: product.article_master?.articleName,
    purity: product.purity,
    wastage: product.wastage, 
    makingCharges: product.makingCharges,
    // Also quantity? weight? etc.
  };
  navigate(`/deal-calculator?data=${encodeURIComponent(JSON.stringify(dealData))}`);
};

  const handleSelectAllSellers = () => {
    if (selectedSellers.length === matchedSellers.length) {
      setSelectedSellers([]);
    } else {
      setSelectedSellers(matchedSellers.map((s) => s.id?.toString()));
    }
  };

  const handleDeleteRequirement = async () => {
    if (!deleteRequirementId) return;
    try {
      const res = await deleteBuyerRequirementApi(deleteRequirementId);
      if (res?.success) {
        toast.success(res.message || "Requirement deleted successfully");
        setRequirements((prev) => prev.filter((item) => item.id !== deleteRequirementId));
        if (selectedReq?.id === deleteRequirementId) setSelectedReq(null);
        setShowDeleteModal(false);
        setDeleteRequirementId(null);
      } else {
        toast.error(res?.message || "Failed to delete requirement");
      }
    } catch (error: any) {
      console.error("Delete error:", error);
      toast.error(error?.response?.data?.message || "Error deleting requirement");
    }
  };

  const handleSellerToggle = (sellerId: string) => {
    setSelectedSellers((prev) =>
      prev.includes(sellerId) ? prev.filter((id) => id !== sellerId) : [...prev, sellerId]
    );
  };

  const handleShareWithSellers = async () => {
    if (!selectedRequirementForShare || selectedSellers.length === 0) return;
    try {
      setLoadingSellers(true);
      const sellerIds = selectedSellers.map((id) => parseInt(id, 10));
      const response = await shareRequirementWithSellers(selectedRequirementForShare.id, sellerIds);
      if (response.success) {
        toast.success(response.message || "Requirement shared successfully");
        saveSharedRequirement(selectedRequirementForShare.id);
        setShowShareModal(false);
        setSelectedRequirementForShare(null);
        setSelectedSellers([]);
        setMatchedSellers([]);
      } else {
        throw new Error(response.message || "Failed to share requirement");
      }
    } catch (error) {
      console.error("Error sharing requirement:", error);
      toast.error(error instanceof Error ? error.message : "Failed to share requirement");
    } finally {
      setLoadingSellers(false);
    }
  };

  const handleOpenDealCalculator = () => {
    if (!selectedRequirementForResponse || !responseSummary) return;
    const acceptedSellersList = responseSummary.sellers?.filter((seller: any) => seller.status?.toLowerCase() === "accepted") || [];
    if (acceptedSellersList.length === 0) {
      toast.warning("No accepted sellers available");
      return;
    }
    const matchedItems = selectedRequirementForResponse.matchedItems || [];
    const responseRequirement = acceptedSellersList.find((s: any) => s.requirement)?.requirement;
    const responseBuyer = acceptedSellersList.find((s: any) => s.buyer)?.buyer;
    const requirementForDeal = {
      ...selectedRequirementForResponse,
      ...(responseRequirement || {}),
      buyerDetails: selectedRequirementForResponse.buyerDetails || (responseBuyer ? {
        id: responseBuyer.id,
        firstName: responseBuyer.firstName,
        businessName: responseBuyer.businessName,
        mobileNumber: responseBuyer.mobileNumber,
      } : undefined),
    };

    const enrichedAcceptedSellers = acceptedSellersList
      .map((sellerResponse: any) => {
        const sellerId = sellerResponse.seller?.id;
        if (!sellerId) return null;
        const matchedItem = sellerResponse.matchedItem || matchedItems.find((item: any) => item.userid === sellerId);
        const sellerInfo = sellerResponse.seller || {};
        let diffValue = 0;
        if (matchedItem?.difference !== undefined && matchedItem?.difference !== null) {
          const diffStr = String(matchedItem.difference);
          diffValue = parseFloat(diffStr) || 0;
        }
        return {
          seller: {
            id: sellerId,
            firstName: sellerInfo.firstName || "",
            lastName: sellerInfo.lastName || "",
            businessName: sellerInfo.businessName || "",
            mobileNumber: sellerInfo.mobileNumber || "",
          },
          buyer: sellerResponse.buyer,
          matchedItem: matchedItem ? { ...matchedItem, difference: diffValue } : null,
          status: sellerResponse.status || "accepted",
          requirement: sellerResponse.requirement || requirementForDeal,
        };
      })
      .filter(Boolean);
    if (enrichedAcceptedSellers.length === 0) {
      toast.warning("No matching items found for accepted sellers");
      return;
    }
    setSelectedRowForDealCalculator(requirementForDeal);
    setAcceptedSellersForDeal(enrichedAcceptedSellers);
    setShowDealCalculatorModal(true);
    setShowResponseModal(false);
  };

  const handleSellerAccept = async () => {
    if (!selectedRequirementForShare?.id) {
      toast.error("No requirement selected");
      return;
    }
    if (selectedSellers.length === 0) {
      toast.error("Please select at least one seller");
      return;
    }
    try {
      const requests = selectedSellers.map((sellerId) =>
        adminRespondRequirementApi({
          status: "Accepted",
          sellerId: parseInt(sellerId),
          requirementId: selectedRequirementForShare.id,
        })
      );
      await Promise.all(requests);
      toast.success("Accepted successfully ✅");
      setShowShareModal(false);
      setSelectedSellers([]);
    } catch (error) {
      console.error(error);
      toast.error("Failed to accept");
    }
  };

  // ---------- Filter & Pagination ----------
  const getCurrentData = () => {
    const data = activeTab === "requirements" ? requirements : enquiries;

    const getBuyerName = (row: any): string => {
      if (activeTab === "requirements") {
        if (row.buyerDetails) {
          return row.buyerDetails.firstName || row.buyerDetails.businessName || "";
        }
      } else {
        if (row.user) {
          return row.user.businessName || `${row.user.firstName || ""} ${row.user.lastName || ""}`.trim() || "";
        }
      }
      return "";
    };

    const getMetalTypeName = (row: any): string => {
      if (activeTab === "requirements") {
        return row.metalTypeDetails?.name || "";
      } else {
        // enquiries: handle different possible field names
        return row.product?.Metal_type?.name || row.product?.DesiredType?.name || "";
      }
    };

    return data.filter((item) => {
      const categoryName = item.category_name || item.product?.category?.name || "";
      const articleName = item.article_name || (item.matchedItems?.[0]?.articleName) || "";
      const buyerName = getBuyerName(item).toLowerCase();
      const metalType = getMetalTypeName(item).toLowerCase();
      const searchLower = search.toLowerCase();

      return (
        categoryName.toLowerCase().includes(searchLower) ||
        articleName.toLowerCase().includes(searchLower) ||
        buyerName.includes(searchLower) ||
        metalType.includes(searchLower)
      );
    });
  };
  const filtered = getCurrentData();
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filtered.slice(startIndex, startIndex + itemsPerPage).map((item, index) => ({
    ...item,
    serial: startIndex + index + 1,
  }));

  // ✅ Requirement columns (fixed article name)
  const requirementColumns = [
    { key: "serial", label: "S.NO", render: (value: number) => <span>{value}</span> },
    {
      key: "buyerDetails",
      label: "Buyer",
      render: (value: any, row: any) => {
        const buyer = getBuyerInfo(row);
        return (
          <div className="buyer-info">
            <div className="buyer-name">{buyer.name}</div>
            <div className="buyer-phone">{buyer.phone}</div>
          </div>
        );
      },
    },
    {
      key: "metalTypeDetails",
      label: " Metal Type",
      render: (value: any, row: any) => <span>{row.metalTypeDetails?.name || "-"}</span>,
    },
    {
      key: "category_name",
      label: "Category",
      render: (value: string, row: any) => <span>{row.category_name || "-"}</span>,
    },
    {
      key: "article_name",
      label: "Article Name",
      render: (_: any, row: any) => <span>{getArticleName(row)}</span>, // ✅ fixed
    },
    {
      key: "quantity",
      label: "Purchasing Quantity(Per/Gram)",
      render: (value: number, row: any) => `${value || 0} `,
    },
    {
      key: "Purity",
      label: "Purity (Carat/Karat)",
      render: (_: string, row: any) => getRequirementPurity(row),
    },
    {
      key: "requirmentStatus",
      label: "Status",
      render: (value: string) => getStatusBadge(value),
    },
  ];

  const enquiryColumns = [
    { key: "serial", label: "S.NO", render: (value: number) => <span>{value}</span> },
    {
      key: "user",
      label: "Buyer",
      render: (value: any, row: any) => {
        const buyer = getBuyerInfo(row);
        return (
          <div className="buyer-info">
            <div className="buyer-name">{buyer.name}</div>
            <div className="buyer-phone">{buyer.phone}</div>
          </div>
        );
      },
    },
    {
      key: "product",
      label: "Product",
      render: (value: any, row: any) => (
        <div>
          {row.product?.category?.name || "-"} - {row.product?.article_master?.articleName || "-"}
        </div>
      ),
    },
    {
      key: "product.metalType",
      label: "Metal Type",
      render: (value: any, row: any) => <span>{row.product?.Metal_type?.name || "-"}</span>,
    },
    {
      key: "message",
      label: "Message",
      render: (_: any, row: any) => {
        const fullMessage = row.message || "";
        const words = fullMessage.split(/\s+/);
        const truncated = words.length > 20
          ? words.slice(0, 20).join(" ") + "..."
          : fullMessage;
        return (
          <span title={fullMessage} style={{ cursor: "pointer" }}>
            {truncated || "-"}
          </span>
        );
      },
    },
    {
      key: "quantity",
      label: "Quantity",
      render: (value: number, row: any) => `${row.quantity || 0} ${row.product?.Uom?.name || ""}`,
    },
    {
      key: "createdAt",
      label: "Enquiry Date",
      render: (value: string, row: any) => <span>{new Date(row.createdAt).toLocaleDateString()}</span>,
    },
  ];

  const handleTabChange = (tab: "requirements" | "enquiries") => {
    setActiveTab(tab);
    setSearch("");
    setCurrentPage(1);
  };

  // ---------- Render ----------
  return (
    <div className="buyer-container">
      <div className="tab-headers" style={{ display: "flex", gap: "2px", borderBottom: "2px solid #374151", marginBottom: "20px" }}>
        <button
          onClick={() => handleTabChange("requirements")}
          style={{
            padding: "12px 24px",
            backgroundColor: activeTab === "requirements" ? "#FBBF24" : "#1F2937",
            color: activeTab === "requirements" ? "#000000" : "#9CA3AF",
            border: "none",
            borderRadius: "8px 8px 0 0",
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: "500",
          }}
        >
          Buyer Requirements
        </button>
        <button
          onClick={() => handleTabChange("enquiries")}
          style={{
            padding: "12px 24px",
            backgroundColor: activeTab === "enquiries" ? "#FBBF24" : "#1F2937",
            color: activeTab === "enquiries" ? "#000000" : "#9CA3AF",
            border: "none",
            borderRadius: "8px 8px 0 0",
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: "500",
          }}
        >
          Buyer Enquiries
        </button>
      </div>

      <div className="header-bar">
        <div className="search-wrapper">
          <input
            type="text"
            placeholder={activeTab === "requirements" ? "Search by article or category..." : "Search enquiries..."}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="search-bar"
          />
        </div>
        <div className="right-actions">
          <div className="pagination-info">
            Showing {paginatedData.length} of {filtered.length} entries
          </div>
        </div>
      </div>

      <div>
        <CommonTable
          columns={activeTab === "requirements" ? requirementColumns : enquiryColumns}
          data={paginatedData}
          tableClassName="buyer-table"
          actions={(row) => {
            if (activeTab === "requirements") {
              return (
                <div className="action-icons">
                  <button className="icon-btn view" title="View Details" onClick={() => setSelectedReq(row)}>
                    <Eye size={18} />
                  </button>
                  {!row.hideSharebutton && (
                    <button className="icon-btn send" title="Share with Sellers" onClick={() => handleShareClick(row)}>
                      <Send size={18} />
                    </button>
                  )}
                  <button
                    className="icon-btn response"
                    title="View Seller Responses"
                    onClick={() => {
                      setSelectedRequirementForResponse(row);
                      setShowResponseModal(true);
                      loadResponseSummary(row.id);
                    }}
                  >
                    <CheckCircle size={18} />
                  </button>
                  <button
                    className="icon-btn delete"
                    title="Delete Requirement"
                    onClick={() => {
                      setDeleteRequirementId(row.id);
                      setShowDeleteModal(true);
                    }}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              );
            } else {
              return (
                <div className="action-icons">
                  <button className="icon-btn view" title="View Details" onClick={() => setSelectedReq(row)}>
                    <Eye size={18} />
                  </button>
                  <button className="icon-btn send" title="Send Enquiry" onClick={() => handleEnquirySendClick(row)}>
                    <Send size={18} />
                  </button>
                </div>
              );
            }
          }}
        />
      </div>

      {totalPages > 1 && (
        <div className="pagination-controls" style={{ display: "flex", justifyContent: "center", gap: "10px", marginTop: "20px", padding: "10px" }}>
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            style={{
              padding: "8px 16px",
              backgroundColor: currentPage === 1 ? "#e5e7eb" : "#FBBF24",
              color: currentPage === 1 ? "#9ca3af" : "#000000",
              border: "none",
              borderRadius: "4px",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
            }}
          >
            Previous
          </button>
          <span style={{ padding: "8px 16px", color: "#fff" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            style={{
              padding: "8px 16px",
              backgroundColor: currentPage === totalPages ? "#e5e7eb" : "#FBBF24",
              color: currentPage === totalPages ? "#9ca3af" : "#000000",
              border: "none",
              borderRadius: "4px",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
            }}
          >
            Next
          </button>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: "400px", width: "90%", padding: "20px", borderRadius: "12px" }}>
            <h3 style={{ marginBottom: "15px" }}>Delete Requirement</h3>
            <p style={{ marginBottom: "25px", color: "#555" }}>Are you sure you want to delete this requirement?</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteRequirementId(null);
                }}
                style={{ padding: "10px 18px", borderRadius: "8px", border: "1px solid #d1d5db", background: "#fff", cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteRequirement}
                style={{ padding: "10px 18px", borderRadius: "8px", border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontWeight: "600" }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && selectedRequirementForShare && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="share-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Share Requirement with Sellers</h2>
              <button className="close-btn" onClick={() => setShowShareModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="requirement-title">
                REQ-{String(selectedRequirementForShare.id).padStart(3, "0")} - {selectedRequirementForShare.category_name}
                ({selectedRequirementForShare.metalTypeDetails?.name || "Metal"}, Jewelry)
              </div>
              <div className="requirement-card">
                <div className="requirement-detail">
                  {selectedRequirementForShare.metalTypeDetails?.name || "Metal"} {selectedRequirementForShare.category_name} -
                  {getRequirementPurity(selectedRequirementForShare)},
                  {selectedRequirementForShare.weight || selectedRequirementForShare.quantity || ""}{getRequirementUom(selectedRequirementForShare)}
                </div>
                <div className="requirement-description">
                  {selectedRequirementForShare.description || "No description provided"}
                </div>
              </div>
              <div className="sellers-section">
                <div className="sellers-header">
                  <h3>Matched Sellers ({matchedSellers.length})</h3>
                  {matchedSellers.length > 0 && (
                    <button className="select-all-btn" onClick={handleSelectAllSellers}>
                      {selectedSellers.length === matchedSellers.length ? "Deselect All" : "Select All"}
                    </button>
                  )}
                </div>
                {loadingSellers ? (
                  <div className="loading-sellers">Loading sellers...</div>
                ) : (
                  <div className="sellers-list">
                    {matchedSellers.length > 0 ? (
                      matchedSellers.map((seller, index) => {
                        const sellerId = seller.id?.toString();
                        const sellerName = seller.businessName ||
                          `${seller.firstName || ""} ${seller.lastName || ""}`.trim() ||
                          "Unknown Seller";
                        return (
                          <div key={sellerId || index} className="seller-item">
                            <label className="seller-label">
                              <input
                                type="checkbox"
                                checked={selectedSellers.includes(sellerId)}
                                onChange={() => handleSellerToggle(sellerId)}
                                disabled={!sellerId}
                              />
                              <span className="seller-name">{sellerName}</span>
                            </label>
                            <div className="seller-details">
                              {/* <span className="seller-categories">
                                {seller.businessName ? `${seller.businessName}` : "Jewelry"}
                              </span> */}
                              <span className="seller-mobile">
                                📞 {seller.mobileNumber || "N/A"}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="no-sellers">No matched sellers found</div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer flex gap-3 justify-end">
                <button
                  onClick={handleSellerAccept}
                  className="px-5 py-2 rounded-lg bg-green-600 text-white font-medium shadow-md hover:bg-green-700 active:scale-95 transition-all duration-200"
                >
                  Accept
                </button>
                <button
                  className="px-5 py-2 rounded-lg bg-gray-200 text-gray-800 font-medium hover:bg-gray-300 transition-all"
                  onClick={() => setShowShareModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="px-5 py-2 rounded-lg bg-blue-600 text-white font-medium shadow-md hover:bg-blue-700 disabled:opacity-50 transition-all"
                  onClick={handleShareWithSellers}
                  disabled={selectedSellers.length === 0 || loadingSellers}
                >
                  Share with {selectedSellers.length} Seller{selectedSellers.length !== 1 ? "s" : ""}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Response Modal */}
      {showResponseModal && selectedRequirementForResponse && (
        <div className="modal-overlay" onClick={() => setShowResponseModal(false)}>
          <div className="response-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "600px", width: "90%" }}>
            <div className="modal-header">
              <h3>Seller Responses - REQ-{String(selectedRequirementForResponse.id).padStart(3, "0")}</h3>
              <button className="close-btn" onClick={() => setShowResponseModal(false)}>×</button>
            </div>
            {loadingResponses ? (
              <div className="loading-sellers" style={{ padding: "40px", textAlign: "center" }}>Loading responses...</div>
            ) : responseSummary ? (
              <>
                <div className="response-summary" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "10px", padding: "20px", backgroundColor: "#f9fafb", borderRadius: "8px", margin: "20px" }}>
                  <div style={{ textAlign: "center" }}><div style={{ fontSize: "20px", fontWeight: "bold", color: "#1F2937" }}>{responseSummary.total || 0}</div><div style={{ fontSize: "12px", color: "#6B7280" }}>Total</div></div>
                  <div style={{ textAlign: "center" }}><div style={{ fontSize: "20px", fontWeight: "bold", color: "#10B981" }}>{responseSummary.accepted || 0}</div><div style={{ fontSize: "12px", color: "#6B7280" }}>Accepted</div></div>
                  <div style={{ textAlign: "center" }}><div style={{ fontSize: "20px", fontWeight: "bold", color: "#EF4444" }}>{responseSummary.rejected || 0}</div><div style={{ fontSize: "12px", color: "#6B7280" }}>Rejected</div></div>
                  <div style={{ textAlign: "center" }}><div style={{ fontSize: "20px", fontWeight: "bold", color: "#F59E0B" }}>{responseSummary.pending || 0}</div><div style={{ fontSize: "12px", color: "#6B7280" }}>Pending</div></div>
                </div>
                <div className="response-list" style={{ padding: "0 20px 20px 20px", maxHeight: "400px", overflowY: "auto" }}>
                  <h4 style={{ margin: "0 0 15px 0", fontSize: "16px", color: "#fff" }}>Seller Details</h4>
                  {responseSummary.sellers && responseSummary.sellers.length > 0 ? (
                    responseSummary.sellers.map((seller: any, index: number) => (
                      <div key={seller.seller?.id || index} className="response-item" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "15px", border: "1px solid #e5e7eb", borderRadius: "8px", marginBottom: "10px", backgroundColor: "#ffffff" }}>
                        <div>
                          <strong style={{ fontSize: "14px", color: "#1F2937" }}>
                            {seller.seller?.businessName || `${seller.seller?.firstName || ""} ${seller.seller?.lastName || ""}`.trim() || "Unknown Seller"}
                          </strong>
                          <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "4px" }}>{seller.seller?.mobileNumber || seller.seller?.phone || "No phone"}</div>
                          {seller.matchedItem && <div style={{ fontSize: "11px", color: "#10B981", marginTop: "4px" }}>Matched: {seller.matchedItem.articleName || "Item"} ({seller.matchedItem.purity})</div>}
                          {!seller.matchedItem && seller.requirement && <div style={{ fontSize: "11px", color: "#10B981", marginTop: "4px" }}>Requirement Purity: {getRequirementPurity(seller.requirement)}</div>}
                          {/* {!seller.matchedItem && <div style={{ fontSize: "11px", color: "#F59E0B", marginTop: "4px" }}>No matching item found</div>} */}
                        </div>
                        <div style={{ padding: "4px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: "500", backgroundColor: seller.status?.toLowerCase() === "accepted" ? "#d1fae5" : seller.status?.toLowerCase() === "rejected" ? "#fee2e2" : seller.status?.toLowerCase() === "pending" ? "#fef3c7" : "#f3f4f6", color: seller.status?.toLowerCase() === "accepted" ? "#065f46" : seller.status?.toLowerCase() === "rejected" ? "#991b1b" : seller.status?.toLowerCase() === "pending" ? "#92400e" : "#374151" }}>
                          {seller.status || "Unknown"}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-sellers" style={{ textAlign: "center", padding: "40px", color: "#6B7280" }}>No seller responses found</div>
                  )}
                </div>
                {responseSummary.sellers && responseSummary.sellers.length > 0 && responseSummary.accepted > 0 ? (
                  <div className="modal-footer" style={{ marginTop: "20px", borderTop: "1px solid #e5e7eb", padding: "20px", display: "flex", justifyContent: "center" }}>
                    <button className="deal-calculator-btn" onClick={handleOpenDealCalculator} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", width: "100%", maxWidth: "300px", padding: "12px", backgroundColor: "#FBBF24", color: "#000000", border: "none", borderRadius: "8px", fontSize: "16px", fontWeight: "600", cursor: "pointer" }}>
                      <Calculator size={20} /> Go to Deal Calculator ({responseSummary.accepted} Accepted)
                    </button>
                  </div>
                ) : (
                  <div className="modal-footer" style={{ marginTop: "20px", borderTop: "1px solid #e5e7eb", padding: "20px", display: "flex", justifyContent: "center" }}>
                    <div style={{ padding: "12px", backgroundColor: "#374151", color: "#9CA3AF", borderRadius: "8px", fontSize: "14px", textAlign: "center", width: "100%", maxWidth: "300px" }}>No accepted sellers available to create deal</div>
                  </div>
                )}
              </>
            ) : (
              <div className="no-sellers" style={{ textAlign: "center", padding: "40px", color: "#6B7280" }}>No response data available</div>
            )}
          </div>
        </div>
      )}

      {/* View Modal */}
      {selectedReq && (
        <div className="modal-overlay" onClick={() => setSelectedReq(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "500px", width: "90%" }}>
            <h3>{activeTab === "requirements" ? "Requirement Details" : "Enquiry Details"}</h3>
            <table className="details-table">
              <tbody>
                <tr><th>Buyer Name</th><td>{getBuyerInfo(selectedReq).name}</td></tr>
                <tr><th>Mobile Number</th><td>{getBuyerInfo(selectedReq).phone}</td></tr>
                {activeTab === "requirements" ? (
                  <>
                    <tr><th>MetalType</th><td>{selectedReq.metalTypeDetails?.name || "-"}</td></tr>
                    <tr><th>Category</th><td>{selectedReq.category_name || "-"}</td></tr>
                    <tr><th>Article Name</th><td>{getArticleName(selectedReq)}</td></tr>
                    <tr><th>Quantity</th><td>{selectedReq.quantity}</td></tr>
                    <tr><th>UOM</th><td>{getRequirementUom(selectedReq)}</td></tr>
                    <tr><th>Purity</th><td>{getRequirementPurity(selectedReq)}</td></tr>
                    {selectedReq.Dimensions && selectedReq.Dimensions.length > 0 && (
                      <>
                        <tr><th>Width</th><td>{selectedReq.Dimensions[0]?.width || "-"}</td></tr>
                        <tr><th>Length</th><td>{selectedReq.Dimensions[0]?.length || "-"}</td></tr>
                        <tr><th>Diameter</th><td>{selectedReq.Dimensions[0]?.diameter || "-"}</td></tr>
                        <tr><th>Thickness</th><td>{selectedReq.Dimensions[0]?.thickness || "-"}</td></tr>
                      </>
                    )}
                    {selectedReq.image && (
                      <tr><th>Image</th><td><img src={`${environment.fileUrl}${selectedReq.image}`} alt="Requirement" style={{ width: "120px", height: "120px", objectFit: "cover", borderRadius: "8px", border: "1px solid #ddd" }} /></td></tr>
                    )}
                    <tr><th>Description</th><td>{selectedReq.description || "-"}</td></tr>
                  </>
                ) : (
                  <>
                    <tr><th>Product</th><td>{selectedReq.product?.category?.name || "-"} - {selectedReq.product?.article_master?.articleName || "-"}</td></tr>
                    <tr><th>Metal Type</th><td>{selectedReq.product?.DesiredType?.name || "-"}</td></tr>
                    <tr><th>Quantity</th><td>{selectedReq.quantity} {selectedReq.product?.Uom?.name || ""}</td></tr>
                    <tr><th>Enquiry Date</th><td>{new Date(selectedReq.createdAt).toLocaleString()}</td></tr>
                    <tr><th>Last Updated</th><td>{new Date(selectedReq.updatedAt).toLocaleString()}</td></tr>
                  </>
                )}
              </tbody>
            </table>
            <button className="add-btn" onClick={() => setSelectedReq(null)} style={{ marginTop: "15px" }}>Close</button>
          </div>
        </div>
      )}

      <DealCalculatorModal
        isOpen={showDealCalculatorModal}
        onClose={() => {
          setShowDealCalculatorModal(false);
          setAcceptedSellersForDeal([]);
        }}
        selectedRow={selectedRowForDealCalculator}
        metalList={metalList}
        getBuyerInfo={getBuyerInfo}
        getMetalName={getMetalName}
        getPurityLabel={getPurityLabel}
        acceptedSellers={acceptedSellersForDeal}
      />
    </div>
  );
};

export default BuyerRequirement;
